import type { BenchmarkContract } from "../config/benchmarkContracts";
import contractsV2Service from "./contractsV2Service";
import type { FactorEntry, FactorsResponse } from "./productFactorsService";

export interface BenchmarkFactorsBatchResult {
  benchmark: BenchmarkContract;
  factors: FactorEntry[];
}

const PAGE_FETCH_CONCURRENCY = 2;
const BATCH_FETCH_CONCURRENCY = 2;
const FACTORS_CACHE_TTL_MS = 30 * 60 * 1000;
const FACTORS_CACHE_SWEEP_INTERVAL_MS = 10 * 60 * 1000;

interface BenchmarkFactorsCacheEntry {
  factors: FactorEntry[];
  fetchedAt: number;
}

async function mapWithConcurrency<T, R>(
  items: T[],
  concurrency: number,
  mapper: (item: T, index: number) => Promise<R>
): Promise<R[]> {
  if (items.length === 0) return [];

  const safeConcurrency = Math.max(1, Math.min(concurrency, items.length));
  const results = new Array<R>(items.length);
  let nextIndex = 0;

  const worker = async () => {
    while (true) {
      const currentIndex = nextIndex;
      nextIndex += 1;
      if (currentIndex >= items.length) return;
      results[currentIndex] = await mapper(items[currentIndex], currentIndex);
    }
  };

  await Promise.all(Array.from({ length: safeConcurrency }, () => worker()));
  return results;
}

class BenchmarkFactorsService {
  private factorsCache = new Map<string, BenchmarkFactorsCacheEntry>();
  private pendingRequests = new Map<string, Promise<FactorEntry[]>>();
  private lastSweepAt = 0;

  async getContractFactors(conId: number, from?: string, to?: string): Promise<FactorEntry[]> {
    this.sweepExpiredEntries();

    const cacheKey = this.cacheKey(conId, from, to);
    const now = Date.now();
    const cached = this.factorsCache.get(cacheKey);
    if (cached && now - cached.fetchedAt < FACTORS_CACHE_TTL_MS) {
      return cached.factors;
    }

    const pending = this.pendingRequests.get(cacheKey);
    if (pending) return pending;

    const promise = this.fetchAllPages(conId, from, to)
      .then((factors) => {
        if (this.pendingRequests.get(cacheKey) === promise) {
          this.factorsCache.set(cacheKey, { factors, fetchedAt: Date.now() });
        }
        return factors;
      })
      .finally(() => {
        if (this.pendingRequests.get(cacheKey) === promise) {
          this.pendingRequests.delete(cacheKey);
        }
      });

    this.pendingRequests.set(cacheKey, promise);
    return promise;
  }

  async getBenchmarkFactorsBatch(
    benchmarks: BenchmarkContract[],
    concurrency: number = BATCH_FETCH_CONCURRENCY
  ): Promise<BenchmarkFactorsBatchResult[]> {
    return mapWithConcurrency(benchmarks, concurrency, async (benchmark) => {
      try {
        const factors = await this.getContractFactors(benchmark.conId);
        return { benchmark, factors };
      } catch (error) {
        console.error(`Failed to fetch benchmark factors for ${benchmark.symbol}`, error);
        return { benchmark, factors: [] };
      }
    });
  }

  private async fetchAllPages(conId: number, from?: string, to?: string): Promise<FactorEntry[]> {
    const firstPage = await contractsV2Service.getContractFactorsPage(conId, 1, from, to);
    const pages: FactorsResponse[] = [firstPage];

    if (firstPage.totalPages > 1) {
      const remainingPageNumbers = Array.from(
        { length: firstPage.totalPages - 1 },
        (_, index) => index + 2
      );
      const remainingPages = await mapWithConcurrency(
        remainingPageNumbers,
        PAGE_FETCH_CONCURRENCY,
        (page) => contractsV2Service.getContractFactorsPage(conId, page, from, to)
      );
      pages.push(...remainingPages);
    }

    return pages
      .flatMap((page) => page.items)
      .sort((a, b) => new Date(a.t).getTime() - new Date(b.t).getTime());
  }

  private cacheKey(conId: number, from?: string, to?: string): string {
    return `${conId}:${from ?? ""}:${to ?? ""}`;
  }

  private sweepExpiredEntries(): void {
    const now = Date.now();
    if (now - this.lastSweepAt < FACTORS_CACHE_SWEEP_INTERVAL_MS) return;
    this.lastSweepAt = now;

    for (const [key, entry] of this.factorsCache) {
      if (now - entry.fetchedAt >= FACTORS_CACHE_TTL_MS) {
        this.factorsCache.delete(key);
      }
    }
  }

  _resetForTesting(): void {
    this.factorsCache.clear();
    this.pendingRequests.clear();
    this.lastSweepAt = 0;
  }
}

export const benchmarkFactorsService = new BenchmarkFactorsService();
